// source: https://github.com/apexcharts/apexcharts.js/blob/main/src/modules/Graphics.js

/**
 * ApexCharts Graphics Class for all drawing operations.
 *
 * @module Graphics
 **/

class ApexGraphics {
  constructor(ctx) {
    this.ctx = ctx;
    this.w = ctx.w;
  }

  getTextBasedOnMaxWidth({ text, maxWidth, fontSize, fontFamily }) {
    const tRects = this.getTextRects(text, fontSize, fontFamily);
    const wordWidth = tRects.width / text.length;
    const wordsBasedOnWidth = Math.floor(maxWidth / wordWidth);
    if (maxWidth < tRects.width) {
      return `${text.slice(0, wordsBasedOnWidth - 3)}...`;
    }
    return text;
  }

  drawText({
    x,
    y,
    text,
    textAnchor,
    fontSize,
    fontFamily,
    fontWeight,
    foreColor,
    opacity,
    maxWidth,
    cssClass = '',
    isPlainText = true,
  }) {
    const w = this.w;

    if (typeof text === 'undefined') {
      text = '';
    }

    let truncatedText = text;
    if (!textAnchor) {
      textAnchor = 'start';
    }

    if (!foreColor || !foreColor.length) {
      foreColor = w.config.chart.foreColor;
    }
    fontFamily = fontFamily || w.config.chart.fontFamily;
    fontSize = fontSize || '11px';
    fontWeight = fontWeight || 'regular';

    const commonProps = {
      maxWidth,
      fontSize,
      fontFamily,
    };
    let elText;
    if (Array.isArray(text)) {
      elText = w.globals.dom.Paper.text(add => {
        for (let i = 0; i < text.length; i++) {
          truncatedText = text[i];
          if (maxWidth) {
            truncatedText = this.getTextBasedOnMaxWidth({
              text: text[i],
              ...commonProps,
            });
          }
          i === 0 ? add.tspan(truncatedText) : add.tspan(truncatedText).newLine();
        }
      });
    } else {
      if (maxWidth) {
        truncatedText = this.getTextBasedOnMaxWidth({
          text,
          ...commonProps,
        });
      }
      elText = isPlainText
        ? w.globals.dom.Paper.plain(text)
        : w.globals.dom.Paper.text(add => add.tspan(truncatedText));
    }

    elText.attr({
      x,
      y,
      'text-anchor': textAnchor,
      'dominant-baseline': 'auto',
      'font-size': fontSize,
      'font-family': fontFamily,
      'font-weight': fontWeight,
      fill: foreColor,
      class: `apexcharts-text ${cssClass}`,
    });

    elText.node.style.fontFamily = fontFamily;
    elText.node.style.opacity = opacity;

    return elText;
  }

  getTextRects(text, fontSize, fontFamily, transform, useBBox = true) {
    const w = this.w;
    const virtualText = this.drawText({
      x: -200,
      y: -200,
      text,
      textAnchor: 'start',
      fontSize,
      fontFamily,
      foreColor: '#fff',
      opacity: 0,
    });

    if (transform) {
      virtualText.attr('transform', transform);
    }
    w.globals.dom.Paper.add(virtualText);

    let rect = virtualText.bbox();
    if (!useBBox) {
      rect = virtualText.node.getBoundingClientRect();
    }

    virtualText.remove();

    return {
      width: rect.width,
      height: rect.height,
    };
  }
}

export default ApexGraphics;
